/**
 * SEO defaults and per-route meta. Used by PageMeta (title, description, canonical, OG).
 */

export const SITE_NAME = "WeddingZVenue";

export const DEFAULT_DESCRIPTION =
  "Discover and book the best wedding venues in Delhi, Noida, Ghaziabad, and across India. WeddingZVenue makes planning your dream wedding simple, affordable, and unforgettable.";

const envOrigin =
  typeof process !== "undefined"
    ? (process.env.REACT_APP_CANONICAL_ORIGIN || "").replace(/\/+$/, "")
    : "";

function metaCanonicalOrigin() {
  if (typeof document === "undefined" || typeof window === "undefined")
    return "";
  const m = document.querySelector('meta[name="wz-canonical-origin"]');
  const raw = m?.getAttribute("content")?.trim();
  if (!raw) return "";
  const h = window.location.hostname || "";
  const local =
    h === "localhost" || h === "127.0.0.1" || h === "[::1]";
  if (local) return "";
  return raw.replace(/\/+$/, "");
}

/** Preferred origin: env, then index.html meta (production), then current host. */
export function getCanonicalOrigin() {
  if (envOrigin) return envOrigin;
  const fromMeta = metaCanonicalOrigin();
  if (fromMeta) return fromMeta;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "";
}

export function normalizeSeoPathname(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

/** Full canonical URL for the current path (no query/hash). */
export function getCanonicalHref(pathname) {
  const origin = getCanonicalOrigin();
  if (!origin) return "";
  const path = normalizeSeoPathname(pathname);
  return `${origin}${path === "/" ? "/" : path}`;
}

const EXACT = {
  "/": {
    title: "Wedding Z Venue | Best Banquet Halls & Wedding Venues",
    description:
      "Find the best banquet halls and wedding venues in Ghaziabad & Delhi with Wedding Z Venue. Compare prices, services & book your perfect venue easily.",
  },
  "/login": {
    title: `Login | ${SITE_NAME}`,
    description: `Sign in to your ${SITE_NAME} account to manage enquiries, shortlists, and profile.`,
  },
  "/forgot-password": {
    title: `Forgot Password | ${SITE_NAME}`,
    description: `Reset your ${SITE_NAME} account password securely.`,
  },
  "/reset-password": {
    title: `Reset Password | ${SITE_NAME}`,
    description: `Choose a new password for your ${SITE_NAME} account.`,
  },
  "/register": {
    title: `Register | ${SITE_NAME}`,
    description: `Create a ${SITE_NAME} account or register your wedding business to reach couples planning their big day.`,
  },
  "/enquiry": {
    title: `Send Enquiry | ${SITE_NAME}`,
    description: `Send a wedding enquiry to venues and vendors through ${SITE_NAME}. Get personalised responses for your event.`,
  },
  "/blog": {
    title: "Wedding Blog & Venue Tips | Wedding Z Venue",
    description:
      "Explore wedding planning tips, banquet hall guides, venue ideas & expert advice for Ghaziabad & Delhi. Read the latest blogs on Wedding Z Venue.",
  },
  "/real-weddings": {
    title: "Real Weddings & Love Stories | Wedding Z Venue",
    description:
      "Explore real wedding stories, photos & inspiration from Ghaziabad & Delhi. Get ideas for decor, venues & planning with Wedding Z Venue.",
  },
  "/photos": {
    title: "Wedding Photos & Venue Gallery | Wedding Z Venue",
    description:
      "Browse stunning wedding photos, decor ideas & venue galleries from Ghaziabad & Delhi. Get inspiration for your perfect wedding with Wedding Z Venue.",
  },
  "/testimonials": {
    title: "Customer Reviews & Testimonials | Wedding Z Venue",
    description:
      "Read real customer reviews for Wedding Z Venue. See what couples say about our venues, vendors & services in Ghaziabad & Delhi.",
  },
  "/why-weddingzvenue": {
    title: "Why Choose Wedding Z Venue | Trusted Wedding Platform",
    description:
      "Discover why couples trust Wedding Z Venue for finding venues & vendors in Ghaziabad & Delhi. Easy booking, verified listings & expert support.",
  },
  "/vendors/category/all": {
    title: "Wedding Vendors in Ghaziabad & Delhi | Wedding Z Venue",
    description:
      "Find top wedding vendors in Ghaziabad & Delhi including photographers, decorators, caterers & more. Compare services & book easily with Wedding Z Venue.",
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${SITE_NAME}`,
    description: `How ${SITE_NAME} collects, uses, and protects your personal information.`,
  },
  "/terms": {
    title: `Terms & Conditions | ${SITE_NAME}`,
    description: `Terms of use for ${SITE_NAME} website and services.`,
  },
  "/cookies": {
    title: `Cookie Policy | ${SITE_NAME}`,
    description: `How ${SITE_NAME} uses cookies and similar technologies.`,
  },
  "/contact": {
    title: `Contact Us | ${SITE_NAME}`,
    description: `Get in touch with ${SITE_NAME} for support, partnerships, or general enquiries.`,
  },
  "/faq": {
    title: `FAQ | ${SITE_NAME}`,
    description: `Frequently asked questions about ${SITE_NAME}, venues, vendors, and bookings.`,
  },
  "/help": {
    title: `Help Center | ${SITE_NAME}`,
    description: `Help articles and guidance for using ${SITE_NAME}.`,
  },
  "/list-your-business": {
    title: `List Your Business | ${SITE_NAME}`,
    description: `List your wedding venue or vendor business on ${SITE_NAME} and reach engaged couples.`,
  },
  "/advertise": {
    title: `Advertise With Us | ${SITE_NAME}`,
    description: `Advertising and partnership opportunities with ${SITE_NAME}.`,
  },
  "/partners": {
    title: `Partners | ${SITE_NAME}`,
    description: `Partner with ${SITE_NAME} to grow your wedding business.`,
  },
  "/profile": {
    title: `My Profile | ${SITE_NAME}`,
    description: `Manage your ${SITE_NAME} profile, enquiries, and saved listings.`,
  },
  "/profile/business": {
    title: `Complete Business Profile | ${SITE_NAME}`,
    description: `Finish your business profile on ${SITE_NAME} to go live for couples.`,
  },
};

/**
 * @returns {{ title: string, description: string, robots?: string }}
 */
export function getSeoMeta(pathname) {
  const path = normalizeSeoPathname(pathname);

  if (EXACT[path]) {
    return EXACT[path];
  }

  if (path.startsWith("/city/")) {
    const raw = path.slice("/city/".length);
    let city = raw;
    try {
      city = decodeURIComponent(raw) || raw;
    } catch {
      city = raw;
    }
    const cityLower = String(city).trim().toLowerCase();
    if (cityLower === "ghaziabad") {
      return {
        title: "Best Wedding Venues in Ghaziabad | Prices, Photos & Reviews",
        description:
          "Discover top wedding venues in Ghaziabad with prices, photos & real reviews. Compare banquet halls, lawns & vendors to book your perfect venue today.",
      };
    }
    if (cityLower === "noida") {
      return {
        title: "Top Wedding Venues in Noida for Every Budget | Book Now",
        description:
          "Find stunning wedding venues in Noida for every budget. Browse banquet halls, luxury venues & lawns with photos, pricing and easy booking options.",
      };
    }
    return {
      title: `Wedding Venues & Vendors in ${city} | ${SITE_NAME}`,
      description: `Explore wedding venues and professional vendors in ${city}. Compare options, view photos, and plan your celebration with ${SITE_NAME}.`,
    };
  }

  if (path === "/venues" || path.startsWith("/venues/")) {
    return {
      title: `Wedding Venues in Delhi NCR | Banquet Halls & Party Lawns`,
      description: `Discover top-rated wedding venues, banquet halls and party lawns across Delhi NCR. Compare prices, check availability and book your perfect venue today.`,
    };
  }

  if (/^\/venue\/[^/]+$/.test(path)) {
    return {
      title: `Venue Details | ${SITE_NAME}`,
      description: `View venue details, amenities, pricing, and photos on ${SITE_NAME}. Enquire directly from the listing.`,
    };
  }

  if (path.startsWith("/vendors/vendor/")) {
    return {
      title: `Vendor Profile | ${SITE_NAME}`,
      description: `View this wedding vendor’s services, packages, and reviews on ${SITE_NAME}.`,
    };
  }

  if (path.startsWith("/vendors")) {
    return {
      title: `Wedding Vendors in Delhi NCR | Photographers, Decorators`,
      description: `Browse verified wedding vendors in Delhi NCR - photographers, decorators, caterers and planners. Compare services, read reviews and book with ease.`,
    };
  }

  if (/^\/real-weddings\/[^/]+$/.test(path)) {
    return {
      title: `Real Wedding Story | ${SITE_NAME}`,
      description: `See photos and details from a real wedding featured on ${SITE_NAME}.`,
    };
  }

  if (/^\/blog\/[^/]+$/.test(path)) {
    return {
      title: `Wedding Blog | ${SITE_NAME}`,
      description: `Read wedding planning tips, inspiration, and stories on ${SITE_NAME}.`,
    };
  }

  if (path.startsWith("/admin")) {
    return {
      title: `Admin | ${SITE_NAME}`,
      description: `${SITE_NAME} administration.`,
      robots: "noindex, nofollow",
    };
  }

  return {
    title: `Page Not Found (404) | ${SITE_NAME}`,
    description: `The page you requested could not be found. Return to ${SITE_NAME} home to explore venues and vendors.`,
    robots: "noindex, follow",
  };
}
